// Iter 75 — Per-vendor Bills tab on VendorDetail.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Receipt, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    BILL_STATUS_BADGE, BILL_DEFAULT_ENUMS,
    QUOTATION_DEFAULT_ENUMS, formatAmount,
} from "./vendor_constants";

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (d) => { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };

export default function VendorBillsTab({ vendor }) {
    const { can } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        api.get(`/vendors/${vendor.id}/bills`)
            .then(({ data }) => { if (!cancelled) setRows(data.items || []); })
            .catch((err) => { if (!cancelled) toast.error(formatApiError(err, "Failed to load bills")); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [vendor.id, reloadKey]);

    if (loading) return <div className="text-sm text-slate-500 py-8 text-center" data-testid="vendor-bills-loading">Loading…</div>;

    return (
        <div data-testid="vendor-bills-tab">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="font-medium text-slate-900">Bills</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        All bills from this vendor (PO-derived + standalone). Standalone bills are recorded for ad-hoc expenses like domain renewals.
                    </p>
                </div>
                {can("vendor:write") && (
                    <Button data-testid="vendor-bill-add-btn" size="sm" onClick={() => setOpen(true)} className="gap-1">
                        <Plus size={14} /> Add Standalone Bill
                    </Button>
                )}
            </div>
            {rows.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-slate-200 rounded-lg" data-testid="vendor-bills-empty">
                    <Receipt size={28} className="text-slate-300 mx-auto mb-2" />
                    <div className="text-sm text-slate-500">No bills recorded</div>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="vendor-bills-table">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="text-left font-medium px-4 py-3">Code</th>
                                <th className="text-left font-medium px-4 py-3">Bill #</th>
                                <th className="text-left font-medium px-4 py-3">Date</th>
                                <th className="text-left font-medium px-4 py-3">Due</th>
                                <th className="text-left font-medium px-4 py-3">PO</th>
                                <th className="text-right font-medium px-4 py-3">Net Payable</th>
                                <th className="text-right font-medium px-4 py-3">Outstanding</th>
                                <th className="text-left font-medium px-4 py-3">Status</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((b) => {
                                const badge = BILL_STATUS_BADGE[b.status] || BILL_STATUS_BADGE.pending;
                                return (
                                    <tr key={b.id} data-testid={`vendor-bill-row-${b.code}`}
                                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                        onClick={() => navigate(`/vendor-bills/${b.id}`)}>
                                        <td className="px-4 py-3 font-mono text-xs text-slate-700">{b.code}</td>
                                        <td className="px-4 py-3 text-xs text-slate-700">{b.bill_number}</td>
                                        <td className="px-4 py-3 text-xs text-slate-700">{formatDate(b.bill_date)}</td>
                                        <td className="px-4 py-3 text-xs text-slate-700">{formatDate(b.due_date)}</td>
                                        <td className="px-4 py-3 text-xs font-mono">{b.vendor_purchase_order_code || <span className="text-slate-300">standalone</span>}</td>
                                        <td className="px-4 py-3 text-right font-mono">{formatAmount(b.net_payable, b.currency)}</td>
                                        <td className="px-4 py-3 text-right font-mono text-xs text-rose-700">{formatAmount(b.outstanding_amount, b.currency)}</td>
                                        <td className="px-4 py-3">
                                            <Badge className={`${badge.className} hover:${badge.className}`}>{badge.label}</Badge>
                                        </td>
                                        <td className="px-4 py-3 text-right"><ArrowRight size={14} className="text-slate-400" /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <StandaloneBillDialog
                key={open ? "open" : "closed"}
                open={open}
                onClose={() => setOpen(false)}
                onSaved={(billId) => { setReloadKey((k) => k + 1); navigate(`/vendor-bills/${billId}`); }}
                vendor={vendor}
            />
        </div>
    );
}

function StandaloneBillDialog({ open, onClose, onSaved, vendor }) {
    const [form, setForm] = useState(() => ({
        bill_number: "",
        bill_date: todayISO(),
        due_date: addDaysISO(30),
        description: "",
        currency: vendor.currency || "INR",
        subtotal: 0,
        gst_amount: 0,
        tds_section: vendor.tds_category || "not_applicable",
        tds_rate: Number(vendor.tds_rate || 0),
        tds_amount: "",
        notes: "",
    }));
    const [busy, setBusy] = useState(false);

    const setField = (k) => (e) => {
        const v = e?.target?.value;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const sub = Number(form.subtotal || 0);
    const gst = Number(form.gst_amount || 0);
    const gross = sub + gst;
    const tdsAuto = Math.round((sub * Number(form.tds_rate || 0) / 100) * 100) / 100;
    const tdsActual = form.tds_amount === "" ? tdsAuto : Number(form.tds_amount || 0);
    const net = Math.round((gross - tdsActual) * 100) / 100;

    const submit = async () => {
        if (!form.bill_number.trim()) { toast.error("Bill number required"); return; }
        if (!form.bill_date || !form.due_date) { toast.error("Dates required"); return; }
        setBusy(true);
        try {
            const r = await api.post(`/vendors/${vendor.id}/bills`, {
                bill_number: form.bill_number,
                bill_date: form.bill_date,
                due_date: form.due_date,
                description: form.description,
                currency: form.currency,
                subtotal: sub,
                gst_amount: gst,
                tds_section: form.tds_section,
                tds_rate: Number(form.tds_rate || 0),
                tds_amount: form.tds_amount === "" ? null : Number(form.tds_amount),
                notes: form.notes,
            });
            toast.success(`Bill ${r.data.code} created`);
            onSaved(r.data.id);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="vendor-bill-dialog">
                <DialogHeader>
                    <DialogTitle>Add Standalone Bill</DialogTitle>
                    <DialogDescription>
                        For ad-hoc vendor bills without a PO. Vendor: {vendor.name} ({vendor.code}).
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
                    <Field label="Bill Number *" colSpan={2}>
                        <Input data-testid="standalone-bill-number" value={form.bill_number} onChange={setField("bill_number")} placeholder="Vendor's invoice ref" />
                    </Field>
                    <Field label="Bill Date *">
                        <Input data-testid="standalone-bill-date" type="date" value={form.bill_date} onChange={setField("bill_date")} />
                    </Field>
                    <Field label="Due Date *">
                        <Input data-testid="standalone-bill-due-date" type="date" value={form.due_date} onChange={setField("due_date")} />
                    </Field>
                    <Field label="Currency">
                        <select value={form.currency} onChange={setField("currency")}
                            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm">
                            {QUOTATION_DEFAULT_ENUMS.currencies.map((c) => (<option key={c} value={c}>{c}</option>))}
                        </select>
                    </Field>
                    <Field label="Subtotal *">
                        <Input data-testid="standalone-bill-subtotal" type="number" min={0} step="0.01" value={form.subtotal} onChange={setField("subtotal")} />
                    </Field>
                    <Field label="GST Amount">
                        <Input data-testid="standalone-bill-gst" type="number" min={0} step="0.01" value={form.gst_amount} onChange={setField("gst_amount")} />
                    </Field>
                    <Field label="TDS Section">
                        <select data-testid="standalone-bill-tds-section" value={form.tds_section} onChange={setField("tds_section")}
                            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm">
                            {BILL_DEFAULT_ENUMS.tds_sections.map((s) => (<option key={s} value={s}>{s}</option>))}
                        </select>
                    </Field>
                    <Field label="TDS Rate %">
                        <Input data-testid="standalone-bill-tds-rate" type="number" min={0} max={30} step="0.01" value={form.tds_rate} onChange={setField("tds_rate")} />
                    </Field>
                    <Field label={`TDS Amount (auto: ${formatAmount(tdsAuto, form.currency)})`} colSpan={2} hint="Leave blank to auto-compute">
                        <Input data-testid="standalone-bill-tds-override" type="number" min={0} step="0.01" value={form.tds_amount} onChange={setField("tds_amount")} />
                    </Field>
                    <Field label="Description" colSpan={2}>
                        <Input data-testid="standalone-bill-desc" value={form.description} onChange={setField("description")} placeholder="e.g. Domain renewal Mar 2026" />
                    </Field>
                    <Field label="Notes" colSpan={2}>
                        <Textarea data-testid="standalone-bill-notes" rows={2} value={form.notes} onChange={setField("notes")} />
                    </Field>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 mt-2" data-testid="standalone-bill-preview">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <Stat label="Gross" value={formatAmount(gross, form.currency)} />
                        <Stat label="TDS" value={formatAmount(tdsActual, form.currency)} />
                        <Stat label="Net Payable" value={formatAmount(net, form.currency)} tone="positive" testid="standalone-bill-net" />
                        <Stat label="Currency" value={form.currency} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="standalone-bill-save-btn" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Create Bill"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, hint, children, colSpan }) {
    return (
        <div className={colSpan === 2 ? "sm:col-span-2" : ""}>
            <Label className="text-xs text-slate-600 mb-1.5 inline-block">{label}</Label>
            {children}
            {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
        </div>
    );
}

function Stat({ label, value, tone, testid }) {
    const toneClass = tone === "positive" ? "text-emerald-700" : "text-slate-900";
    return (
        <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
            <div data-testid={testid} className={`mt-1 font-semibold font-mono ${toneClass}`}>{value}</div>
        </div>
    );
}
